import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Heading, Badge, AccordionItem, AccordionTrigger, Accordion, AccordionContent } from "@aidkitorg/component-library";
import type { FollowUpResult, NotificationResult, NotificationsWorkflowResult } from "@aidkitorg/aidkit/lib/distro/review/workflows/notificationsWorkflow";
import type { TargetFieldsWorkflowResult } from "@aidkitorg/aidkit/lib/distro/review/workflows/targetFieldsWorkflow";
import WorkflowReview from "./WorkflowReview";
import { usePost } from "../API";
import { DISTRO_COMMIT_WORKFLOW_STATUS, WorkflowRow } from "@aidkitorg/aidkit/lib/distro/review/distroReview";
import { ansiToReact } from "./ChangesWorkflowReview";

type ChangedDependency = {
  field: string;
  type: ("formula" | "content" | "conditional")[];
  changeCount: number;
  topChange: { before: string; after: string; count: number; };
};

// NotificationCard component for rendering individual notification cards
function NotificationCard({ 
  result, 
  type, 
  index,
  targetFieldsWorkflow
}: {
  result: NotificationResult & { changedDependencies: ChangedDependency[] } | FollowUpResult;
  type: NotificationResult['changeType'];
  index: number;
  targetFieldsWorkflow: {
    result: TargetFieldsWorkflowResult | undefined;
    status: DISTRO_COMMIT_WORKFLOW_STATUS;
  } | null | undefined;
}) {
  const notification = result.newNotification || result.oldNotification;
  if (!notification) {
    return null;
  }
  const target = 'targetPrefix' in notification ? notification.targetPrefix : notification.suffix;
  const isFollowUp = 'suffix' in notification;

  type MethodResult = { count?: number; error?: string; query?: string };
  type ByMethod = { email?: MethodResult; sms?: MethodResult; whatsapp?: MethodResult };

  const getCountDifference = (newCount: number | undefined, oldCount: number | undefined) => {
    const diff = (newCount || 0) - (oldCount || 0);
    if (diff === 0) return null;
    return diff > 0 ? `+${diff}` : `${diff}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const hasError = (surveyResult: NotificationResult['newSurveyResult'] | NotificationResult['oldSurveyResult']): boolean => {
    return !!(surveyResult && 'error' in surveyResult);
  };

  const renderBadges = () => {
    if (type === 'changed' && result.newSurveyResult && 'enabled' in result.newSurveyResult && result.oldSurveyResult && 'enabled' in result.oldSurveyResult && result.newSurveyResult.enabled !== result.oldSurveyResult.enabled) {
      return (
        <>
          {renderEnablementBadge(result.oldSurveyResult)} → {renderEnablementBadge(result.newSurveyResult)}
        </>
      );
    }
    
    const surveyResult = type === 'removed' ? result.oldSurveyResult : result.newSurveyResult;
    return renderEnablementBadge(surveyResult);
  };

  const renderEnablementBadge = (result: NotificationResult['newSurveyResult'] | NotificationResult['oldSurveyResult'] | null) => {
    if (!result) return null;
    if ('error' in result) return <Badge variant="error" className="text-xs">error</Badge>;
    if (result.enabled === undefined) return <Badge variant="warning" className="text-xs">unknown</Badge>;
    
    return (
      <Badge variant={result.enabled ? 'success' : 'error'} className="text-xs">
        {result.enabled ? 'Enabled' : 'Disabled'}
      </Badge>
    );
  };

  const renderDependencies = (changedDependencies: ChangedDependency[]) => {
    if (!changedDependencies || changedDependencies.length === 0) return null;

    return (
      <div className="mt-3">
        <div className="overflow-x-auto">
          <table className="w-auto text-xs">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 pr-4 font-medium text-gray-600">Target Field</th>
                <th className="text-left py-2 pr-4 font-medium text-gray-600">Location</th>
                <th className="text-right py-2 pr-4 font-medium text-gray-600">Affected Applicants</th>
                <th className="text-left py-2 font-medium text-gray-600">Top Change</th>
              </tr>
            </thead>
            <tbody>
              {changedDependencies.map(({ field, type, changeCount, topChange }) => {
                return (
                  <tr key={field} className="border-b border-gray-100">
                    <td className="py-2 pr-4">
                      <Badge variant={type.includes('content') ? 'info' : 'neutral'} className="text-xs">
                        {field}
                      </Badge>
                    </td>
                    <td className="py-2 pr-4 text-gray-600">{type.join(', ')}</td>
                    <td className="py-2 pr-4 text-center">
                      <span className="font-medium text-orange-600">
                        {changeCount.toLocaleString()}
                      </span>
                    </td>
                    <td className="py-2">
                      {topChange ? (
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-mono px-1 py-0.5 bg-orange-100 border border-orange-200 rounded text-orange-800">
                            {topChange.before || 'null'}
                          </span>
                          <span className="text-gray-400">→</span>
                          <span className="font-mono px-1 py-0.5 bg-blue-100 border border-blue-200 rounded text-blue-800">
                            {topChange.after || 'null'}
                          </span>
                          <span className="text-gray-500">({topChange.count})</span>
                        </div>
                      ) : (
                        <span className="text-gray-400">No changes</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const getBorderClass = () => {
    switch (type) {
      case 'new': return 'rounded-2xl border-2 border-green-500';
      case 'removed': return 'rounded-2xl border-2 border-red-500';
      case 'changed': return 'rounded-2xl border-2 border-yellow-500';
      case 'unchanged': return 'rounded-2xl border-gray-300';
      case 'error': return 'rounded-2xl border-2 border-red-500';
      default: return '';
    }
  };


  const getBadgeVariant = () => {
    switch (type) {
      case 'new': return 'success';
      case 'removed': return 'error';
      case 'changed': return 'warning';
      case 'unchanged': return 'neutral';
      case 'error': return 'error';
      default: return 'neutral';
    }
  };

  const renderMethodRow = (
    method: 'email' | 'sms' | 'whatsapp',
    oldResult: MethodResult | undefined,
    newResult: MethodResult | undefined,
    showComparison: boolean
  ) => {
    const oldCount = oldResult?.count;
    const newCount = newResult?.count;
    const diff = showComparison ? getCountDifference(newCount, oldCount) : null;
    const isIncrease = (newCount || 0) > (oldCount || 0);
    const hasChanged = oldCount !== newCount;
    const error = newResult?.error || oldResult?.error;
    const query = newResult?.query;

    return (
      <div key={method} className="border-b border-gray-100 last:border-b-0 py-2">
        <div className="flex items-center gap-2">
          <span className="w-20 font-medium">{method}:</span>
          {error ? (
            <span className="text-red-600 text-xs">{error}</span>
          ) : (
            <>
              <span>
                {showComparison ? (
                  <>{oldCount ?? 0} {hasChanged ? `→ ${newCount ?? 0}` : ''} eligible</>
                ) : (
                  <>{newCount ?? oldCount ?? 0} eligible</>
                )}
              </span>
              {diff && (
                <span className={`font-medium ${isIncrease ? 'text-green-600' : 'text-red-600'}`}>
                  ({diff})
                </span>
              )}
            </>
          )}
          {query && (
            <button
              onClick={() => copyToClipboard(query)}
              className="ml-auto text-xs text-blue-600 hover:text-blue-800 hover:underline"
              title="Copy SQL query to clipboard"
            >
              Copy SQL
            </button>
          )}
        </div>
        {query && (
          <Accordion type="single" className="mt-1">
            <AccordionItem value="query" className="border-0">
              <AccordionTrigger className="py-1 text-xs text-gray-500 hover:no-underline">
                Show query
              </AccordionTrigger>
              <AccordionContent>
                <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto max-h-40 overflow-y-auto">
                  {query}
                </pre>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
      </div>
    );
  };

  const renderContent = () => {
    const newByMethod = result.newSurveyResult && 'byMethod' in result.newSurveyResult ? result.newSurveyResult.byMethod : undefined;
    const oldByMethod = result.oldSurveyResult && 'byMethod' in result.oldSurveyResult ? result.oldSurveyResult.byMethod : undefined;

    // Get all methods present in either old or new
    const allMethods = (['email', 'sms', 'whatsapp'] as const).filter(
      method => (newByMethod && method in newByMethod) || (oldByMethod && method in oldByMethod)
    );

    if (type === 'new') {
      return (
        <div className="text-sm text-gray-600">
          {(result.newSurveyResult && 'error' in result.newSurveyResult) ? (
            <span className="text-red-600">Error: {result.newSurveyResult.error}</span>
          ) : (
            <div>
              {isFollowUp && <div className="text-gray-500 mb-2">after {notification.after.amount} {notification.after.unit}</div>}
              {allMethods.map(method => renderMethodRow(method, undefined, newByMethod?.[method], false))}
            </div>
          )}
        </div>
      );
    }

    if (type === 'removed') {
      return (
        <div className="text-sm text-gray-600">
          {(result.oldSurveyResult && 'error' in result.oldSurveyResult) ? (
            <span className="text-red-600">Error: {result.oldSurveyResult.error}</span>
          ) : (
            <div>
              {allMethods.map(method => renderMethodRow(method, oldByMethod?.[method], undefined, false))}
            </div>
          )}
        </div>
      );
    }

    // unchanged and changed
    return (
      <div className="text-sm text-gray-600">
        {(hasError(result.oldSurveyResult) || hasError(result.newSurveyResult)) && (
          <div className="flex flex-col gap-1 mb-2">
            <div className={`text-sm ${hasError(result.oldSurveyResult) ? 'text-red-600' : 'text-gray-600'}`}>
              {(result.oldSurveyResult && 'error' in result.oldSurveyResult) ? `Old: ${result.oldSurveyResult.error}` : 'Old: no error'}
            </div>
            <div className={`text-sm ${hasError(result.newSurveyResult) ? 'text-red-600' : 'text-green-600'}`}>
              {(result.newSurveyResult && 'error' in result.newSurveyResult) ? `New: ${result.newSurveyResult.error}` : 'New: no error'}
            </div>
          </div>
        )}
        {result.changeType !== 'error' && (
          <div>
            {isFollowUp && <div className="text-gray-500 mb-2">after {notification.after.amount} {notification.after.unit}</div>}
            {allMethods.map(method => renderMethodRow(method, oldByMethod?.[method], newByMethod?.[method], true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card key={index} className={getBorderClass()}>
      <CardBody>
        <div className="font-medium flex items-center gap-2 mb-2">
          <Heading level={isFollowUp ? "h6" : "h4"} size='h4' className="mb-1">
            {'name' in notification && notification.name && <span className="pr-2">{notification.name}</span>} ({target})
          </Heading>
          {renderBadges()}
          {isFollowUp && <span className="flex w-full justify-end"><Badge variant={getBadgeVariant()} className="text-xs">{result.changeType}</Badge></span>}
        </div>
        {renderContent()}
        {'followups' in result && result.followups.length > 0 && (
          <div className="mt-3">
            <Heading level="h5" className="mb-1">Follow ups</Heading>
            <div className="flex flex-col gap-2">
              {result.followups.map((followUp) => (
                <NotificationCard
                  key={followUp.newNotification?.suffix || followUp.oldNotification?.suffix}
                  result={followUp}
                  type={followUp.changeType}
                  index={index}
                  targetFieldsWorkflow={targetFieldsWorkflow}
                />
              ))}
            </div>
          </div>
        )}
        {type !== 'removed' && 'changedDependencies' in result && renderDependencies(result.changedDependencies)}
        {'diff' in result && <Accordion type="single" value="diff">
          <AccordionItem value="diff">
            <AccordionTrigger>
              <span className="text-xs text-gray-500">Show changes</span>
            </AccordionTrigger>
            <AccordionContent>
              {result.diff && (
                <pre className="mt-3 border border-gray-200 bg-gray-50 rounded-md p-2 overflow-x-auto">
                  {ansiToReact(result.diff).segments.map((segment, index) => (
                    <React.Fragment key={index}>{segment}</React.Fragment>
                  ))}
                </pre>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>}
      </CardBody>
    </Card>
  );
}

// NotificationView component handles workflow display and status polling
export default function NotificationsWorkflowReview({ distroCommitId }: { distroCommitId: string }) {
  const getWorkflow = usePost('/distro-review/workflow');
  const [targetFieldsWorkflow, setTargetFieldsWorkflow] = useState<{
    result: TargetFieldsWorkflowResult | undefined;
    status: DISTRO_COMMIT_WORKFLOW_STATUS;
  } | null>(null);

  // Fetch target fields workflow on mount and poll while pending/in-progress
  useEffect(() => {
    if (!distroCommitId) return;

    const loadTargetFieldsWorkflow = async () => {
      try {
        const result = await getWorkflow({ 
          workflowName: 'target_fields',
          distroCommitId
        });
        if (!result.error && result.workflow) {
          setTargetFieldsWorkflow({
            result: result.workflow.result as TargetFieldsWorkflowResult | undefined,
            status: result.workflow.status
          });
        }
      } catch (error) {
        console.error('Error loading target fields workflow:', error);
      }
    };

    loadTargetFieldsWorkflow();

    const pollInterval = setInterval(() => {
      if (targetFieldsWorkflow?.status === 'pending' || targetFieldsWorkflow?.status === 'in_progress') {
        loadTargetFieldsWorkflow();
      } else {
        clearInterval(pollInterval);
      }
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [getWorkflow, distroCommitId, targetFieldsWorkflow?.status]);

  const targetFieldsMap = useMemo(() => {
    if (!targetFieldsWorkflow?.result?.success || !targetFieldsWorkflow?.result?.targetFields) {
      return {};
    }
    return targetFieldsWorkflow.result.targetFields.reduce((acc, field) => {
      acc[field.key] = field;
      return acc;
    }, {} as Record<string, TargetFieldsWorkflowResult['targetFields'][number]>);
  }, [targetFieldsWorkflow]);

  const renderNotificationResults = (workflow: { result: NotificationsWorkflowResult | undefined }) => {
    if (!workflow?.result?.success || !workflow.result.notificationResults) {
      return null;
    }

    const results = workflow.result.notificationResults;
    
    const categorizedResults = (() => {
      const newNotifications: (NotificationResult & { changedDependencies: ChangedDependency[] })[] = [];
      const removedNotifications: (NotificationResult & { changedDependencies: ChangedDependency[] })[] = [];
      const changedNotifications: (NotificationResult & { changedDependencies: ChangedDependency[] })[] = [];
      const unchangedNotifications: (NotificationResult & { changedDependencies: ChangedDependency[] })[] = [];
      const errorNotifications: (NotificationResult & { changedDependencies: ChangedDependency[] })[] = [];

      for (const result of results) {
        const newResult: NotificationResult & { changedDependencies: ChangedDependency[] } = {...result, changedDependencies: []};
        if (newResult.newSurveyResult && !('error' in newResult.newSurveyResult) && newResult.newSurveyResult.dependencies) {
          newResult.changedDependencies = newResult.newSurveyResult.dependencies.map(({ field, type }) => {
            const fieldData = targetFieldsMap[field];
            const changeCount = fieldData?.countChangedApplicants || 0;
            const topChange = fieldData?.beforeAfterValues?.[0];
            return { field, type, changeCount, topChange };
          }).filter(({ changeCount }) => changeCount > 0);
        }
        switch (result.changeType) {
          case 'new':
            newNotifications.push(newResult);
            break;
          case 'removed':
            removedNotifications.push(newResult);
            break;
          case 'changed':
            changedNotifications.push(newResult);
            break;
          case 'unchanged':
            unchangedNotifications.push(newResult);
            break;
          case 'error':
            errorNotifications.push(newResult);
            break;
        }
      }
      return {
        newNotifications,
        removedNotifications,
        changedNotifications,
        unchangedNotifications,
        errorNotifications
      };
    })();

    // Count per-method SQL errors across all notifications
    const countMethodErrors = (results: NotificationResult[]) => {
      let count = 0;
      for (const result of results) {
        const surveyResult = result.newSurveyResult || result.oldSurveyResult;
        if (surveyResult && 'byMethod' in surveyResult) {
          const byMethod = surveyResult.byMethod;
          if (byMethod.email?.error) count++;
          if (byMethod.sms?.error) count++;
          if (byMethod.whatsapp?.error) count++;
        }
        // Also count followup errors
        for (const followup of result.followups || []) {
          const followupResult = followup.newSurveyResult || followup.oldSurveyResult;
          if (followupResult && 'byMethod' in followupResult) {
            const byMethod = followupResult.byMethod;
            if (byMethod.email?.error) count++;
            if (byMethod.sms?.error) count++;
            if (byMethod.whatsapp?.error) count++;
          }
        }
      }
      return count;
    };

    // Calculate summary
    const methodErrorCount = countMethodErrors(results);
    const summary = {
      totalNotifications: results.length,
      newNotifications: categorizedResults.newNotifications.length,
      removedNotifications: categorizedResults.removedNotifications.length,
      changedNotifications: categorizedResults.changedNotifications.length,
      unchangedNotifications: categorizedResults.unchangedNotifications.length,
      notificationErrorCount: categorizedResults.errorNotifications.length,
      methodErrorCount
    };

    return (
      <div className="space-y-6 mb-16">
        {/* Summary */}
        <Card>
          <CardBody>
            <div className="font-medium flex flex-col gap-1">
              <div>{summary.totalNotifications} total notifications</div>
              {summary.newNotifications > 0 && <div className="text-green-600">{summary.newNotifications} new</div>}
              {summary.removedNotifications > 0 && <div className="text-red-600">{summary.removedNotifications} removed</div>}
              {summary.changedNotifications > 0 && <div className="text-yellow-600">{summary.changedNotifications} changed</div>}
              {summary.unchangedNotifications > 0 && <div className="text-gray-500">{summary.unchangedNotifications} unchanged</div>}
              {(summary.notificationErrorCount > 0 || summary.methodErrorCount > 0) && (
                <div className="text-red-600 mt-2">
                  {summary.notificationErrorCount > 0 && <div>{summary.notificationErrorCount} notification errors</div>}
                  {summary.methodErrorCount > 0 && <div>{summary.methodErrorCount} SQL query errors</div>}
                </div>
              )}
            </div>
            <div className="text-sm mt-4">
              <p>
                Note: notifications with recipient type "Screener" are <span className="font-bold">not yet supported</span> by the review tool
                and will not be shown in these results.
              </p>
            </div>
          </CardBody>
        </Card>

        {/* New Notifications */}
        {categorizedResults.newNotifications.length > 0 && (
          <div className="space-y-4">
            <Heading level="h3" className="text-md font-semibold">
              New Notifications ({categorizedResults.newNotifications.length})
            </Heading>
            {categorizedResults.newNotifications.map((result, index: number) => (
              <NotificationCard
                key={index}
                result={result}
                type="new"
                index={index}
                targetFieldsWorkflow={targetFieldsWorkflow}
              />
            ))}
          </div>
        )}
          
        {/* Removed Notifications */}
        {categorizedResults.removedNotifications.length > 0 && (
          <div className="space-y-4">
            <Heading level="h3" className="font-semibold">
              Removed Notifications ({categorizedResults.removedNotifications.length})
            </Heading>
            {categorizedResults.removedNotifications.map((result, index: number) => (
              <NotificationCard
                key={index}
                result={result}
                type="removed"
                index={index}
                targetFieldsWorkflow={targetFieldsWorkflow}
              />
            ))}
          </div>
        )}

        {/* Changed Notifications */}
        {categorizedResults.changedNotifications.length > 0 && (
          <div className="space-y-4">
            <Heading level="h3" className="font-semibold">
              Changed Notifications ({categorizedResults.changedNotifications.length})
            </Heading>
            {categorizedResults.changedNotifications.map((result, index: number) => (
              <NotificationCard
                key={index}
                result={result}
                type="changed"
                index={index}
                targetFieldsWorkflow={targetFieldsWorkflow}
              />
            ))}
          </div>
        )}

        {/* Error Notifications */}
        {categorizedResults.errorNotifications.length > 0 && (
          <div className="space-y-4">
            <Heading level="h3" className="font-semibold">
              Error Notifications ({categorizedResults.errorNotifications.length})
            </Heading>
            {categorizedResults.errorNotifications.map((result, index: number) => (
              <NotificationCard
                key={index}
                result={result}
                type="error"
                index={index}
                targetFieldsWorkflow={targetFieldsWorkflow}
              />
            ))}
          </div>
        )}

        {/* Unchanged Notifications */}
        {categorizedResults.unchangedNotifications.length > 0 && (
          <div className="space-y-4">
            <Heading level="h3" className="font-semibold">
              Unchanged Notifications ({categorizedResults.unchangedNotifications.length})
            </Heading>
            {categorizedResults.unchangedNotifications.sort((a, b) => b.changedDependencies.length - a.changedDependencies.length).map((result, index: number) => (
              <NotificationCard
                key={index}
                result={result}
                type="unchanged"
                index={index}
                targetFieldsWorkflow={targetFieldsWorkflow}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <WorkflowReview
      distroCommitId={distroCommitId}
      workflowName="notifications"
      title="Notifications Analysis"
      renderSummary={renderNotificationResults}
    />
  );
};
